
import { createContext, useState, useEffect, useContext, useRef, ReactNode } from "react";
import * as Sentry from "@sentry/react";
import posthog from 'posthog-js';
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  accessSharedCloset: (username: string, pagePassword: string) => Promise<boolean>;
  sharedAccess: { userId: string, username: string } | null;
  clearSharedAccess: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Enhanced security: Helper function to sanitize user inputs
const sanitizeInput = (input: string): string => {
  return input.replace(/[<>]/g, '');
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [sharedAccess, setSharedAccess] = useState<{ userId: string, username: string } | null>(null);
  const [hasRedirectedOnSignIn, setHasRedirectedOnSignIn] = useState(false);
  const hasRedirectedRef = useRef(false);
  const navigate = useNavigate();

  // Function to clean up auth state
  const cleanupAuthState = () => {
    // Remove standard auth tokens
    localStorage.removeItem('supabase.auth.token');
    // Remove all Supabase auth keys from localStorage
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    // Also remove from sessionStorage if in use
    Object.keys(sessionStorage || {}).forEach((key) => {
      if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });

    // Clear secure cookie if we have access to document
    if (typeof document !== 'undefined') {
      document.cookie = "auth-session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; secure; HttpOnly; SameSite=Strict";
    }
  };

  useEffect(() => {
    // Check for stored shared access on load
    const storedSharedAccess = localStorage.getItem('sharedAccess');
    if (storedSharedAccess) {
      try {
        setSharedAccess(JSON.parse(storedSharedAccess));
      } catch (error) {
        localStorage.removeItem('sharedAccess');
      }
    }

    // Check "Remember me" preference - if user didn't check it and this is a new browser session, sign out
    const rememberMe = localStorage.getItem('rememberMe');
    const sessionActive = sessionStorage.getItem('sessionActive');

    if (rememberMe === 'false' && !sessionActive) {
      // User didn't want to be remembered and this is a new browser session
      // Sign them out
      supabase.auth.signOut().then(() => {
        localStorage.removeItem('rememberMe');
        cleanupAuthState();
      });
    } else if (rememberMe !== null) {
      // Mark this browser session as active
      sessionStorage.setItem('sessionActive', 'true');
    }

    // Set up auth state listener. The callback MUST stay synchronous: awaiting
    // Supabase calls inside it can deadlock the auth library's navigator.locks
    // on page refresh. We only update session state here and defer any DB work
    // to setTimeout(0) (per Supabase's documented guidance).
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Handle initial session load (fires on page refresh/load)
        if (event === 'INITIAL_SESSION') {
          if (newSession?.user) {
            posthog.identify(newSession.user.id, { email: newSession.user.email });
            setTimeout(() => createProfileIfNeeded(newSession.user), 0);
          }
          setIsLoading(false);
          return;
        }

        if (event === 'SIGNED_IN') {
          if (newSession?.user) {
            Sentry.setUser({ id: newSession.user.id, email: newSession.user.email ?? undefined });
            posthog.identify(newSession.user.id, { email: newSession.user.email });
          }
          if (newSession?.user && !hasRedirectedRef.current) {
            // Redirect decisions read only in-memory data, so they stay on the
            // synchronous path. Only redirect if we're on an auth/callback page —
            // this preserves deep links when refreshing (e.g. /dashboard4).
            const currentPath = window.location.pathname;
            const isAuthPage = currentPath === '/auth' || currentPath === '/' || currentPath === '/login'
              || currentPath === '/redirecting' || currentPath === '/dashboard';

            if (isAuthPage) {
              // Navigate to /redirecting which resolves the correct dashboard
              navigate('/redirecting', { replace: true });
            }
            hasRedirectedRef.current = true;
            setHasRedirectedOnSignIn(true);

            // Profile lookup/creation + welcome toast hit the DB — defer them
            // out of the auth callback to avoid the navigator.locks deadlock.
            setTimeout(async () => {
              // Check if this is an OAuth user (Google)
              const isOAuthUser = newSession.user.app_metadata?.provider === 'google';

              if (isOAuthUser) {
                // Check if user has an existing profile
                const { data: existingProfile } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', newSession.user.id)
                  .maybeSingle();

                if (!existingProfile) {
                  // Auto-create profile for new Google user (whether login or signup flow)
                  sessionStorage.removeItem('google_signup_flow');
                  const { error: profileError } = await supabase
                    .from('profiles')
                    .insert([{
                      id: newSession.user.id,
                      email: newSession.user.email,
                      full_name: newSession.user.user_metadata?.full_name || newSession.user.user_metadata?.name || '',
                      avatar_url: newSession.user.user_metadata?.avatar_url || '',
                      updated_at: new Date().toISOString()
                    }]);

                  if (profileError) {
                    console.error('Error creating profile for Google user:', profileError);
                  }
                  toast.success("Welcome to Cello! Your account has been created.");
                } else {
                  sessionStorage.removeItem('google_signup_flow');
                  toast.success("Signed in successfully");
                }
              } else {
                // Email/password sign in
                toast.success("Signed in successfully");
              }

              // Ensure profile exists
              createProfileIfNeeded(newSession.user);
            }, 0);
          }
        }
        if (event === 'SIGNED_OUT') {
          Sentry.setUser(null);
          posthog.reset();
          toast.success("Signed out successfully");
          navigate('/');

          // Reset redirect tracking on sign out
          hasRedirectedRef.current = false;
          setHasRedirectedOnSignIn(false);

          // Security enhancement: Clear more state data
          cleanupAuthState();
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        createProfileIfNeeded(currentSession.user);
      }

      setIsLoading(false);
    }).catch((err) => {
      console.error('Error restoring session:', err);
      Sentry.captureException(err, { tags: { context: 'restore_session' } });
      setIsLoading(false);
    });

    // Safety net: if auth hasn't resolved in 3 seconds (e.g. navigator.locks
    // deadlock on page refresh), force isLoading to false so the app isn't stuck
    const safetyTimeout = setTimeout(() => {
      setIsLoading((current) => {
        if (current) {
          console.warn('Auth loading safety timeout — forcing isLoading to false');
        }
        return false;
      });
    }, 3000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(safetyTimeout);
    };
  }, [navigate]);

  const createProfileIfNeeded = async (user: User) => {
    try {
      // Security: Add rate limiting for this function
      const cacheKey = `profile_check_${user.id}`;
      const lastCheck = sessionStorage.getItem(cacheKey);

      if (lastCheck && (Date.now() - parseInt(lastCheck, 10)) < 60000) {
        // Limit checks to once per minute per user
        return;
      }

      sessionStorage.setItem(cacheKey, Date.now().toString());

      // Check if profile exists
      const { data, error } = await supabase
        .from('profiles')
        .select('id, email')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking profile:', error);
        Sentry.captureException(error, { tags: { context: 'check_profile' } });
        return;
      }

      // If profile doesn't exist, create it
      if (!data) {
        // Use upsert which is safer with the new RLS policy
        const { error: insertError } = await supabase
          .from('profiles')
          .upsert({
            id: user.id,
            email: user.email || '',
            username: sanitizeInput(user.email?.split('@')[0] || ''),
            full_name: sanitizeInput(user.user_metadata?.full_name || ''),
            avatar_url: user.user_metadata?.avatar_url || '',
          }, { onConflict: 'id' });

        if (insertError) {
          console.error('Error creating profile:', insertError);
          Sentry.captureException(insertError, { tags: { context: 'create_profile' } });
        }
      } else if (!data.email && user.email) {
        // Backfill email for existing profiles missing it
        await supabase
          .from('profiles')
          .update({ email: user.email })
          .eq('id', user.id);
      }
    } catch (error) {
      console.error('Error in createProfileIfNeeded:', error);
    }
  };

  const signIn = async (email: string, password: string, rememberMe: boolean = true): Promise<{ error?: string }> => {
    try {
      // Input validation
      if (!email || !email.includes('@') || !password || password.length < 6) {
        return { error: "Please provide a valid email and password (min 6 characters)" };
      }

      // UX feedback only — shows a countdown after repeated failures in the same tab.
      // Actual rate limiting is enforced server-side by Supabase Auth (see config.toml [auth.rate_limit]).
      const loginAttemptsKey = `login_attempts_${email}`;
      const loginAttempts = parseInt(sessionStorage.getItem(loginAttemptsKey) || '0', 10);

      if (loginAttempts >= 5) {
        const lastAttemptTime = parseInt(sessionStorage.getItem(`login_last_attempt_${email}`) || '0', 10);
        const timeElapsed = Date.now() - lastAttemptTime;

        if (timeElapsed < 300000) { // 5 minutes
          const minutesLeft = Math.ceil((300000 - timeElapsed) / 60000);
          return { error: `Too many failed attempts. Please try again in ${minutesLeft} minutes.` };
        } else {
          sessionStorage.setItem(loginAttemptsKey, '0');
        }
      }

      const { error } = await supabase.auth.signInWithPassword({
        email: sanitizeInput(email),
        password
      });

      if (error) {
        // Increment failed login attempts
        sessionStorage.setItem(loginAttemptsKey, (loginAttempts + 1).toString());
        sessionStorage.setItem(`login_last_attempt_${email}`, Date.now().toString());

        if (error.message.includes('Invalid login credentials')) {
          return { error: "Invalid email or password. Please try again." };
        } else if (error.message.includes('Email not confirmed')) {
          return { error: "Please check your email and click the confirmation link before signing in." };
        } else {
          return { error: error.message };
        }
      }

      // Reset login attempts on success
      sessionStorage.removeItem(loginAttemptsKey);
      sessionStorage.removeItem(`login_last_attempt_${email}`);

      // Store "Remember me" preference
      localStorage.setItem('rememberMe', rememberMe.toString());
      // Mark current browser session as active
      sessionStorage.setItem('sessionActive', 'true');

      return {};
    } catch (error) {
      console.error("Sign in error:", error);
      Sentry.captureException(error, { tags: { context: 'sign_in' } });
      return { error: error instanceof Error ? error.message : "An unexpected error occurred. Please try again." };
    }
  };

  const signOut = async () => {
    try {
      // Clean up auth state first
      cleanupAuthState();

      // Clear remember me preference
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('sessionActive');

      // Attempt global sign out (fallback if it fails)
      try {
        await supabase.auth.signOut({ scope: 'global' });
      } catch (err) {
        // Continue even if global sign out fails
        console.warn("Global sign out failed, continuing with local sign out");
      }

      // Clear shared access
      clearSharedAccess();

      // Force page reload for a completely clean state
      window.location.href = '/';
    } catch (error) {
      console.error("Sign out error:", error);
      // Even if there's an error, force cleanup and redirect
      cleanupAuthState();
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('sessionActive');
      clearSharedAccess();
      window.location.href = '/';
    }
  };

  const accessSharedCloset = async (username: string, pagePassword: string) => {
    try {
      const sanitizedUsername = sanitizeInput(username);

      if (!sanitizedUsername || sanitizedUsername !== username) {
        toast.error("Invalid username format");
        return false;
      }

      // Rate limiting and hash comparison are enforced server-side in the edge function
      const { data, error } = await supabase.functions.invoke('verify-closet-password', {
        body: { username: sanitizedUsername, password: pagePassword },
      });

      if (error) throw error;

      if (data?.error) {
        toast.error(data.error); // includes 429 "Too many attempts" message
        return false;
      }

      if (!data?.valid) {
        toast.error("Incorrect password for this closet");
        return false;
      }

      const accessInfo = { userId: data.user_id, username: data.username };
      setSharedAccess(accessInfo);
      localStorage.setItem('sharedAccess', JSON.stringify(accessInfo));
      toast.success(`You now have access to ${data.username}'s closet`);
      return true;
    } catch (error) {
      console.error("Error accessing shared closet:", error);
      Sentry.captureException(error, { tags: { context: 'shared_closet_access' } });
      toast.error(error instanceof Error ? error.message : "Error accessing shared closet");
      return false;
    }
  };

  const clearSharedAccess = () => {
    setSharedAccess(null);
    localStorage.removeItem('sharedAccess');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        isLoading,
        signIn,
        signOut,
        accessSharedCloset,
        sharedAccess,
        clearSharedAccess,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
